// src/components/notifications/NotificationPanelContent.tsx
// Notification panel content for use with @levelup/ui-shell
//
// Shell owns the bell icon, badge, and panel container.
// This component provides the panel content (tabs, list, mark-read actions).

import React, { useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { CentralNotification, GroupedNotification } from '@levelup/central-notifications';
import {
  useGetPlatformGroupedInboxQuery,
  useGetPlatformInboxGroupQuery,
  useMarkPlatformNotificationReadMutation,
  useMarkPlatformBulkReadMutation,
  useMarkPlatformGroupReadMutation,
} from '../../api/platformNotificationsApi';
import { APP_ID } from '../../config/centralAuth';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../../config/notifications';
import { GroupedNotificationItem, SourceAppBadge } from './GroupedNotificationItem';
import type { NotificationPanelProps } from '@levelup/ui-shell';

// ============================================================
// Icons
// ============================================================

const BellIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ExternalLinkIcon = ({ className = '' }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

// ============================================================
// Types
// ============================================================

type TabKey = 'thisApp' | 'workspace';

// ============================================================
// NotificationItem Component
// ============================================================

interface NotificationItemProps {
  notification: CentralNotification;
  onMarkRead: () => void;
  showAppIndicator?: boolean;
  onClose?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  showAppIndicator = false,
  onClose,
}) => {
  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkRead();
    }
    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank', 'noopener,noreferrer');
      onClose?.();
    }
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-3 cursor-pointer transition-colors
        ${notification.isRead ? 'bg-white' : 'bg-blue-50'}
        hover:bg-gray-50
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
        {notification.appMetadata?.appIconUrl ? (
          <img
            src={notification.appMetadata.appIconUrl}
            alt={notification.appMetadata.appDisplayName}
            className="w-8 h-8 rounded"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="text-xl">{getDefaultAppIcon(notification.appId)}</span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {showAppIndicator && (
          <SourceAppBadge
            appId={notification.appId}
            appMetadata={notification.appMetadata}
          />
        )}
        <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-xs text-gray-500 truncate">{notification.body}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-400">{timeAgo}</span>
          {notification.actionLabel && (
            <span className="text-xs text-blue-600 flex items-center gap-0.5">
              {notification.actionLabel}
              <ExternalLinkIcon className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>

      {!notification.isRead && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead();
          }}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 rounded"
          title="Mark as read"
        >
          <CheckIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

function getDefaultAppIcon(appId: string | null): string {
  if (!appId) return '🔔';
  switch (appId) {
    case 'crm':
      return '📊';
    case 'levelup-central':
      return '🏢';
    case 'levelup-docs':
      return '📄';
    case 'levelup-payments':
      return '💳';
    default:
      return '📱';
  }
}

// ============================================================
// NotificationList Component
// ============================================================

interface NotificationListProps {
  appId?: string;
  showAppIndicator?: boolean;
  onClose?: () => void;
}

const NotificationList: React.FC<NotificationListProps> = ({
  appId,
  showAppIndicator = false,
  onClose,
}) => {
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  // Workspace is derived server-side from the session — no workspaceId here.
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetPlatformGroupedInboxQuery(
    { appId, limit: 20, grouped: true, enriched: true },
    { refetchOnMountOrArgChange: true, pollingInterval: NOTIFICATION_POLL_INTERVAL_MS }
  );

  const {
    data: groupDetailData,
    isLoading: isLoadingGroupDetail,
  } = useGetPlatformInboxGroupQuery(
    { correlationId: expandedGroupId || '' },
    { skip: !expandedGroupId }
  );

  const [markRead] = useMarkPlatformNotificationReadMutation();
  const [markBulkRead] = useMarkPlatformBulkReadMutation();
  const [markGroupRead] = useMarkPlatformGroupReadMutation();

  const items = data?.items ?? [];
  const hasMore = data?.hasMore ?? false;

  const handleMarkRead = useCallback(async (id: string) => {
    await markRead(id);
  }, [markRead]);

  const handleMarkGroupRead = useCallback(async (correlationId: string) => {
    await markGroupRead({ correlationId });
  }, [markGroupRead]);

  const handleMarkAllRead = useCallback(async () => {
    await markBulkRead({ appId });
  }, [markBulkRead, appId]);

  const handleNotificationClick = useCallback((notification: CentralNotification) => {
    if (!notification.isRead) {
      markRead(notification.id);
    }
    if (notification.actionUrl) {
      window.open(notification.actionUrl, '_blank', 'noopener,noreferrer');
      onClose?.();
    }
  }, [markRead, onClose]);

  const handleExpandGroup = useCallback((correlationId: string) => {
    setExpandedGroupId(correlationId);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      refetch();
    }
  }, [data?.nextCursor, refetch]);

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-2">Loading notifications...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <BellIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p className="font-medium">No notifications</p>
        <p className="text-sm mt-1">
          {appId ? "You're all caught up in this app" : "You're all caught up across all apps"}
        </p>
      </div>
    );
  }

  const unreadCount = items.reduce((count, item) => {
    return count + (item.data.isRead ? 0 : 1);
  }, 0);

  return (
    <>
      {unreadCount > 0 && (
        <div className="px-4 py-2 border-b border-gray-100 flex justify-end">
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Mark all read
          </button>
        </div>
      )}

      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {items.map((item) => {
          if (item.kind === 'group') {
            const group = item.data as GroupedNotification;
            return (
              <GroupedNotificationItem
                key={group.correlationId}
                group={group}
                onMarkGroupRead={() => handleMarkGroupRead(group.correlationId)}
                onNotificationClick={handleNotificationClick}
                expandedNotifications={
                  expandedGroupId === group.correlationId
                    ? groupDetailData?.notifications
                    : undefined
                }
                isLoadingExpanded={
                  expandedGroupId === group.correlationId && isLoadingGroupDetail
                }
                onExpand={() => handleExpandGroup(group.correlationId)}
              />
            );
          } else {
            const notification = item.data as CentralNotification;
            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={() => handleMarkRead(notification.id)}
                showAppIndicator={showAppIndicator}
                onClose={onClose}
              />
            );
          }
        })}
      </div>

      {hasMore && (
        <div className="border-t border-gray-100 p-2">
          <button
            onClick={handleLoadMore}
            disabled={isFetching}
            className="w-full py-2 text-sm text-center text-blue-600 hover:bg-gray-50 rounded disabled:opacity-50"
          >
            {isFetching ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
};

// ============================================================
// Main NotificationPanelContent Component
// ============================================================

/**
 * Notification panel content for the Suite Shell.
 *
 * Shell owns:
 * - Bell icon with badge (HeaderBar)
 * - Panel container (opens on bell click)
 * - Open/close state
 *
 * This component owns:
 * - Panel content (header, tabs, notification list)
 * - Data fetching (RTK Query + SSE)
 * - Mark-read actions
 */
export const NotificationPanelContent: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('thisApp');

  // Don't render if the panel is closed. Workspace is derived server-side by the
  // BFF, so the browser needs no workspaceId. Realtime is polled (PR-N1); the
  // same-origin SSE proxy is a PR-N2 follow-up.
  if (!isOpen) {
    return null;
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'thisApp', label: 'This App' },
    { key: 'workspace', label: 'Workspace' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <h3 className="font-semibold text-gray-900">Notifications</h3>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`
              flex-1 px-4 py-2.5 text-sm font-medium transition-colors
              ${activeTab === tab.key
                ? 'text-blue-600 border-b-2 border-blue-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
              }
            `}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div role="tabpanel" className="flex-1 overflow-hidden">
        {activeTab === 'thisApp' ? (
          <NotificationList
            appId={APP_ID}
            showAppIndicator={false}
            onClose={onClose}
          />
        ) : (
          <NotificationList
            showAppIndicator={true}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default NotificationPanelContent;
